import {naturalSort} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useState, useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {BENCHLING_APPS_ANALYTICS_ACTIONS} from 'constants/analytics-products/BenchlingAppsAnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import {OptionData} from 'ui-library/menus/MenuConstants';
import uniqueId from 'util/uniqueId';

import * as BenchlingAppActions from '../BenchlingAppActions';
import {getWebhookPayloadData, verifyWebhookURL, WebhookPlaygroundPayloadResponse} from '../BenchlingAppDAO';
import {GetWebhookResponse} from './__generated/GetWebhookResponse';
import {AppDefinition} from './BenchlingAppsPage';
import WebhookPlaygroundPreview from './WebhookPlaygroundPreview';
import WebhookPlaygroundResponse, {
  WebhookPlaygroundLoading,
  WebhookPlaygroundInitResponse,
} from './WebhookPlaygroundResponse';

export type WebhookResponse = ExtractByTypename<GetWebhookResponse['webhookResponse'], 'WebhookResponseNode'>;

export enum ResponseState {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  RESPONSE = 'RESPONSE',
}

interface BenchlingAppWebhookPlaygroundProps {
  appDefinition: AppDefinition;
}

export default function BenchlingAppWebhookPlayground(
  props: BenchlingAppWebhookPlaygroundProps
): JSX.Element {
  const [isWebhookURLApp, setIsWebhookURLApp] = useState<boolean>(true);
  const [webhookURL, setWebhookURL] = useState<string | null>(props.appDefinition.webhookUrl);
  const appIsInstalled = props.appDefinition.installation != null;
  const appDefinitionId = props.appDefinition.id;

  async function onUpdateWebhookURL(newWebhookURL: string): Promise<void> {
    await verifyWebhookURL(newWebhookURL);
    setWebhookURL(newWebhookURL);
    notifications.success(<>Successfully updated webhook URL.</>);
  }

  useEffect(() => {
    analytics.trackEvent(BENCHLING_APPS_ANALYTICS_ACTIONS.BENCHLING_APP_WEBHOOK_PLAYGROUND_PAGE_OPENED);
  }, []);

  return (
    <div>
      <Header1>Webhook Testing</Header1>
      <div className={css(styles.marginBottom)}>
        Test webhook deliveries and verify how your app responds. You can also browse message shapes to inform
        how you develop your app code to handle event triggers.&nbsp;
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://docs.benchling.com/docs/getting-started-with-webhooks"
        >
          <u>Learn more</u>
        </a>
      </div>

      <FieldLabel
        htmlFor={uniqueId('webhook-url-field')}
        label="Test Webhook URL"
        labelStyle={FieldLabel.STYLES.LABEL}
      />
      <div className={css(styles.editableField)}>
        <RadioButtonSelector
          selectedValue={isWebhookURLApp}
          options={[
            {label: 'Use app webhook URL', value: true},
            {label: 'Use custom webhook URL', value: false},
          ]}
          isVertical={false}
          onChange={(value) => {
            setIsWebhookURLApp(value);
            if (value) {
              setWebhookURL(props.appDefinition.webhookUrl);
            }
          }}
        />
        <div className={css(styles.webhookInput)}>
          <EditableTextField
            requestChange={onUpdateWebhookURL}
            type={TextFieldType.TEXT}
            value={webhookURL ?? 'None'}
            isFullWidth={true}
            isDisabled={isWebhookURLApp}
          />
        </div>
      </div>
      <div className={css(styles.urlDescription, styles.marginBottom)}>
        {isWebhookURLApp
          ? `Manage app webhook URL on the "Overview" tab.`
          : `Send tests to a custom webhook URL.`}
      </div>

      {appIsInstalled ? (
        <WebhookPlaygroundForm webhookURL={webhookURL} appDefinitionId={appDefinitionId} />
      ) : (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="Testing isn't available."
          supportingInfo="To start testing your app, install this app on your tenant."
        />
      )}
    </div>
  );
}

interface WebhookPlaygroundFormProps {
  webhookURL: string | null;
  appDefinitionId: string;
}

export function WebhookPlaygroundForm(props: WebhookPlaygroundFormProps): JSX.Element {
  const [selectedMessageType, setSelectedMessageType] = useState<string | null>(null);
  const [messageTypes, setMessageTypes] = useState<Array<OptionData>>([]);
  const [payloadData, setPayloadData] = useState<WebhookPlaygroundPayloadResponse | null>(null);
  const [svixMessageId, setSvixMessageId] = useState<string | null>(null);
  const [responseState, setResponseState] = useState<ResponseState>(ResponseState.INITIAL);
  const [responseData, setResponseData] = useState<WebhookResponse | null>(null);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      let sortedMessageTypes: Array<string> = [];
      if (selectedMessageType == null) {
        // Only run on render
        sortedMessageTypes = await BenchlingAppActions.getMessageTypes().then(naturalSort);
        setMessageTypes(sortedMessageTypes.map((messageType) => ({value: messageType, name: messageType})));
      }

      setSelectedMessageType((messageType) => {
        let newMessageType: string | null = messageType;
        if (messageType == null && sortedMessageTypes.length > 0) {
          newMessageType = sortedMessageTypes[0]; // Make default the first message type alphabetically on render
        }

        if (newMessageType != null) {
          getWebhookPayloadData(props.appDefinitionId, newMessageType, props.webhookURL).then(setPayloadData);
        }
        return newMessageType;
      });
    };
    loadData();
  }, [props.appDefinitionId, selectedMessageType, props.webhookURL]);

  async function handleClickSendTest(): Promise<void> {
    if (payloadData == null) {
      return;
    }
    analytics.trackEvent(BENCHLING_APPS_ANALYTICS_ACTIONS.SENT_PLAYGROUND_WEBHOOK, {
      eventType: selectedMessageType,
    });

    setResponseState(ResponseState.LOADING);

    const returnedMessageId = await BenchlingAppActions.sendTestMessage(
      props.appDefinitionId,
      payloadData.payload,
      props.webhookURL!
    );

    setSvixMessageId(returnedMessageId);
  }

  return (
    <div>
      <Row className={css(styles.topRow)}>
        <FormCol className={css(styles.leftCol)}>Preview</FormCol>
        <FormCol>Test</FormCol>
      </Row>
      <Row className={css(styles.bottomRow)}>
        <FormCol className={css(styles.leftCol)}>
          {selectedMessageType != null && payloadData != null && (
            <WebhookPlaygroundPreview
              messageTypes={messageTypes}
              selectedMessageType={selectedMessageType}
              setSelectedMessageType={setSelectedMessageType}
              webhookURL={props.webhookURL}
              payloadData={payloadData}
              handleClickSendTest={handleClickSendTest}
              responseState={responseState}
            />
          )}
        </FormCol>
        <FormCol>
          {responseState === ResponseState.INITIAL ? (
            <WebhookPlaygroundInitResponse />
          ) : responseState === ResponseState.LOADING ? (
            <WebhookPlaygroundLoading
              appDefinitionId={props.appDefinitionId}
              svixMessageId={svixMessageId}
              setResponseState={setResponseState}
              setResponseData={setResponseData}
              setSvixMessageId={setSvixMessageId}
            />
          ) : (
            <WebhookPlaygroundResponse responseData={responseData!} />
          )}
        </FormCol>
      </Row>
    </div>
  );
}

interface FormWrapperProps {
  className?: string;
  children: ReactNode;
}

export function FormCol(props: FormWrapperProps): JSX.Element {
  return (
    <Col md={6} className={classNames(props.className, css(styles.col))}>
      {props.children}
    </Col>
  );
}

const styles = StyleSheet.create({
  bottomRow: {
    borderWidth: '0px 1px 1px 1px',
    borderStyle: 'solid',
    borderColor: GRAY_LIGHT,
    display: 'flex',
    alignItems: 'stretch',
  },
  col: {
    padding: '12px',
  },
  editableField: {
    width: '50%',
    marginTop: '8px',
  },
  leftCol: {
    borderRight: '1px solid',
    borderColor: GRAY_LIGHT,
  },
  marginBottom: {
    marginBottom: '16px',
  },
  topRow: {
    backgroundColor: GRAY_LIGHTER,
    fontWeight: 'bold',
    color: GRAY_SEMIDARK,
    border: '1px solid',
    borderColor: GRAY_LIGHT,
    marginBottom: '0',
  },
  urlDescription: {
    color: GRAY_SEMIDARK,
  },
  webhookInput: {
    minWidth: '480px',
  },
});
